<template>
  <router-view />
  <ProgressSpinner
    v-if="isLoading"
    class="absolute inset-0 flex justify-center items-center h-[unset]"
  />
  <GlobalDialog />
  <GlobalToast />
  <ConfirmDialog />
  <SafeBlockUI full-screen :blocked="stableIsLoading" />
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core'
import ProgressSpinner from 'primevue/progressspinner'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import SafeBlockUI from '@/components/common/SafeBlockUI.vue'
import GlobalDialog from '@/components/dialog/GlobalDialog.vue'
import GlobalToast from '@/components/toast/GlobalToast.vue'
import config from '@/config'
import { useAuthStore } from '@/stores/authStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

import { useConflictDetection } from './composables/useConflictDetection'
import { useTaskErrorNotification } from './composables/useTaskErrorNotification'
import { electronAPI, isElectron } from './utils/envUtil'

const workspaceStore = useWorkspaceStore()
const authStore = useAuthStore()
const conflictDetection = useConflictDetection()
const { stopListening: stopTaskErrorListening } = useTaskErrorNotification()
const isLoading = computed<boolean>(
  () => workspaceStore.spinner || !authStore.isInitialized
)

// 稳定的加载状态，避免频繁变化导致 BlockUI 错误
const stableIsLoading = ref(isLoading.value)
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let isUnmounted = false
let isUpdating = false

// 监听 isLoading 变化，使用防抖机制
watch(
  isLoading,
  async (newValue) => {
    // 如果组件已卸载或正在更新，不执行任何更新
    if (isUnmounted || isUpdating) {
      return
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    // 如果状态从 false 变为 true，立即更新
    if (newValue && !stableIsLoading.value) {
      stableIsLoading.value = true
    } else if (!newValue && stableIsLoading.value) {
      // 如果状态从 true 变为 false，延迟更新以避免 DOM 操作冲突
      isUpdating = true
      debounceTimer = setTimeout(async () => {
        // 再次检查组件是否已卸载
        if (!isUnmounted) {
          // 等待下一个 tick 确保 DOM 更新完成
          await nextTick()
          stableIsLoading.value = newValue
          isUpdating = false
        } else {
          isUpdating = false
        }
      }, 200) as ReturnType<typeof setTimeout>
    }
  },
  { immediate: true }
)
const authInitializing = ref(true)
const handleKey = (e: KeyboardEvent) => {
  workspaceStore.shiftDown = e.shiftKey
}

useEventListener(window, 'keydown', handleKey)
useEventListener(window, 'keyup', handleKey)

const showContextMenu = (event: MouseEvent) => {
  const { target } = event
  switch (true) {
    case target instanceof HTMLTextAreaElement:
    case target instanceof HTMLInputElement && target.type === 'text':
      // TODO: Context input menu explicitly for text input
      electronAPI()?.showContextMenu({ type: 'text' })
      return
  }
}

onMounted(async () => {
  // @ts-expect-error fixme ts strict error
  window['__COMFYUI_FRONTEND_VERSION__'] = config.app_version
  console.log('ComfyUI Front-end version:', config.app_version)

  if (isElectron()) {
    document.addEventListener('contextmenu', showContextMenu)
  }

  try {
    // Initialize authentication state first
    console.log('App.vue: Initializing authentication...')
    // Skip login dialog if we're already on the login page
    const skipLoginDialog = window.location.pathname.includes('/login')
    await authStore.initialize(skipLoginDialog)
    console.log('App.vue: Authentication initialized', {
      isAuthenticated: authStore.isAuthenticated,
      hasToken: !!authStore.getAccessToken(),
      hasUser: !!authStore.currentUser,
      isInitialized: authStore.isInitialized
    })

    // Wait for initialization to be marked as complete
    while (!authStore.isInitialized) {
      console.log('App.vue: Waiting for auth initialization to complete...')
      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    console.log(
      'App.vue: Auth initialization completed, isInitialized:',
      authStore.isInitialized
    )
  } catch (error) {
    console.error('App.vue: Failed to initialize authentication:', error)
  } finally {
    authInitializing.value = false
  }

  // Wait a bit to ensure UI is stable before hiding loading
  await new Promise((resolve) => setTimeout(resolve, 100))

  // Initialize conflict detection in background
  // This runs async and doesn't block UI setup
  void conflictDetection.initializeConflictDetection()
})

// 清理定时器和监听器
onUnmounted(() => {
  isUnmounted = true
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  // 停止任务错误通知监听
  stopTaskErrorListening()
})
</script>

<style>
* {
  --el-color-primary: #ff7e07;
  --el-color-primary-light-3: #ff9634;
  --el-color-primary-light-5: #ffae63;
  --el-color-primary-light-7: #ffc590;
  --el-color-primary-light-8: #ffddbe;
  --el-color-primary-light-9: #fff5ec;
  --el-color-primary-dark-2: #d48806;
  --el-color-primary-dark-3: #b58105;
  --el-color-primary-dark-4: #a37d04;
  --el-color-primary-dark-5: #8b6b04;
  --el-color-primary-dark-6: #492f17;
  --p-button-text-primary-hover-background: var(--el-color-primary-light-8);
  --p-button-text-primary-active-background: var(--el-color-primary-light-5);
  --p-button-text-primary-color: var(--el-color-primary-light-3);

  --p-primary-0: #ffffff;
  --p-orange-50: #fff5ec;
  --p-orange-100: #ffddbe;
  --p-orange-200: #ffc590;
  --p-orange-300: #ffae63;
  --p-orange-400: #ff9634;
  --p-orange-500: #ff7e07;
  --p-orange-600: #d48806;
  --p-orange-700: #b58105;
  --p-orange-800: #a37d04;
  --p-orange-900: #8b6b04;
  --p-orange-950: #492f17;
  --p-primary-50: var(--p-orange-50);
  --p-primary-100: var(--p-orange-100);
  --p-primary-200: var(--p-orange-200);
  --p-primary-300: var(--p-orange-300);
  --p-primary-400: var(--p-orange-400);
  --p-primary-500: var(--p-orange-500);
  --p-primary-600: var(--p-orange-600);
  --p-primary-700: var(--p-orange-700);
  --p-primary-800: var(--p-orange-800);
  --p-primary-900: var(--p-orange-900);
  --p-primary-950: var(--p-orange-950);
  /* --p-surface-0: #ffffff;
  --p-surface-50: #f8fafc;
  --p-surface-100: #f1f5f9;
  --p-surface-200: #e2e8f0;
  --p-surface-300: #cbd5e1;
  --p-surface-400: #94a3b8;
  --p-surface-500: #64748b;
  --p-surface-600: #475569;
  --p-surface-700: #334155;
  --p-surface-800: #1e293b;
  --p-surface-900: #0f172a;
  --p-surface-950: #020617;
  --p-primary-color: var(--p-primary-500);
  --p-primary-contrast-color: var(--p-surface-0);
  --p-primary-hover-color: var(--p-primary-400);
  --p-primary-active-color: var(--p-primary-600); */
  /* --p-surface-50: #faf9f7;
  --p-surface-100: #f5f3f0;
  --p-surface-200: #ebe7e0;
  --p-surface-300: #d6d0c4;
  --p-surface-400: #b8b0a0;
  --p-surface-500: #9a8f7c;
  --p-surface-600: #7c6f5e;
  --p-surface-700: #5e5447;
  --p-surface-800: #403930;
  --p-surface-900: #221e19;
  --p-surface-950: #0a0806;
  --p-primary-color: var(--p-primary-500);
  --p-primary-contrast-color: var(--p-surface-0);
  --p-primary-hover-color: var(--p-primary-400);
  --p-primary-active-color: var(--p-primary-600); */
}
</style>
